import { lazy } from "react";
import type { ComponentType, LazyExoticComponent } from "react";

// THE ROUTE TABLE. Four routes and a 404 — everything else is deferred, and nothing in the deferred list may
// be built into v1 because it would be useful.
//
// `label` is a CLOSED SET because it is a METRIC LABEL. An open label set is how a stage label taken from free
// text produced new time series at 20/s; a route label taken from the URL would do the same, with a mint in it.
// `tests/router.test.ts` asserts the union and this table agree, in both directions.

export type RouteLabel = "token" | "wallet" | "board" | "launches" | "notfound";

export const ROUTE_LABELS: readonly RouteLabel[] = [
  "token",
  "wallet",
  "board",
  "launches",
  "notfound",
] as const;

/** What a matched route hands its screen. `params` is the path's own captures; `query` is the route's state, so
 *  a configured view is LINKABLE — the property worth preserving, and the reason this stays hash-based
 *  rather than moving to the history API (which would need `basename` + nginx `try_files` + Vite `base` kept in
 *  step, three places to get wrong for three routes). */
export interface Route {
  label: RouteLabel;
  params: Readonly<Record<string, string>>;
  query: URLSearchParams;
}

/** A route definition. `pattern` is the full segment list including its leading literal; a segment written
 *  `:name` captures into `params`. One place per route, so adding one is one entry. */
export interface RouteDef {
  label: Exclude<RouteLabel, "notfound">;
  pattern: readonly string[];
  component: LazyExoticComponent<ComponentType<{ route: Route }>>;
}

// Lazily loaded so the entry chunk is the shell and nothing else: the chart (`lightweight-charts` +
// `@kinetiq/ui`), the signing SDKs and the pre-sign decoder are separate chunks, and keeping them out of the
// first paint is precisely what makes the pre-sign guard affordable against the 80 KB gz budget.
export const ROUTES: readonly RouteDef[] = [
  {
    label: "token",
    pattern: ["token", ":mint"],
    component: lazy(() => import("../features/token/TokenPage.tsx")),
  },
  {
    label: "wallet",
    pattern: ["wallet"],
    component: lazy(() => import("../features/wallet/WalletPage.tsx")),
  },
  {
    label: "board",
    pattern: ["board"],
    // THE RANKED BOARD — `/v1/top` seeded once, then live over the `top` channel. Its spec (`?sort=&window=
    // &limit=`) is route state, so a configured board is linkable.
    component: lazy(() => import("../features/board/BoardPage.tsx")),
  },
  {
    label: "launches",
    pattern: ["launches"],
    // THE LAUNCH FEED — `/v1/launches` seeded once, then live over the `launches` channel. A DIFFERENT
    // POPULATION from the board rather than a filter of it: this feed's members are mints whose BIRTH we
    // witnessed, the board's are mints that have TRADED. (The note that used to sit on the board's entry —
    // "the launch dashboard needs a genesis feed the store cannot serve yet, witness coverage ~2.6%" — is
    // spent: the store serves the ring, the route serves the page, and the channel pushes the births.)
    component: lazy(() => import("../features/launches/LaunchesPage.tsx")),
  },
];

/** Where `#/` goes. A redirect and not an alias, so the address bar always states which screen you are on. */
export const HOME_ROUTE = "#/board";

/** The launch feed's address, spelled once so a control's href and the route table cannot disagree. */
export const LAUNCHES_ROUTE = "#/launches";

export const NOT_FOUND = lazy(() => import("./NotFound.tsx"));
